import type { ReactNode } from "react";

/**
 * One line that says what is happening, for the eye and for the ear.
 *
 * Task `10.3`. Both places in this application that ask a model are slow in a
 * way nothing else here is — measured on 2026-09-18, fifteen seconds for a
 * drinking window and twenty-five to thirty for a meal — and both said so with
 * nothing but a greyed-out button. Two things were wrong with that.
 *
 * **It did not say how long.** A button that reads *Frågar…* is honest and
 * tells you nothing about whether to wait or to put the phone down. The
 * sentence that sets the expectation is the whole of this component's visible
 * job, and it costs a line.
 *
 * **It said nothing at all to somebody listening.** `role="status"` is an
 * `aria-live="polite"` region: a screen reader reads its contents when they
 * change, at the next pause, without interrupting. Until this existed, the
 * application announced its *failures* — `role="alert"` on every error message,
 * forty-eight of them — and was silent about its successes. Somebody who
 * pressed the button heard nothing for half a minute and then nothing again
 * when five wines appeared two hundred pixels below.
 *
 * `role="status"` rather than `alert`, on the reasoning `CacheNotice` already
 * wrote down: it is worth saying and not worth interrupting for.
 *
 * **The same element carries the waiting and the arrival**, which is what makes
 * it work: a live region announces a *change* to itself, so a second element
 * appearing elsewhere would be as silent as the rows are. It also puts the
 * sentence where the answer will be, so the eye is already in the right place
 * when the answer replaces it.
 */
export function StatusLine({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="text-muted mt-4 max-w-prose text-sm">
      {children}
    </p>
  );
}
